import { access } from "node:fs/promises";
import { resolve } from "node:path";
import type { ComponentIR } from "./ir.js";
import { ComponentValidationError } from "./errors.js";

export { ComponentValidationError };

export interface ValidationOptions {
  /** Base directory for resolving relative implementation paths. */
  basePath: string;
}

export interface ImplementationIds {
  workflowIds: string[];
  taskIds: string[];
}

function extractIdsFromSection(section: unknown): string[] {
  if (!section || typeof section !== "object" || Array.isArray(section)) {
    return [];
  }

  // Exclude `$ref`/`$refs` and other directive keys. resolve() should already
  // expand them, but stay defensive in case a raw doc slips through.
  return Object.keys(section as Record<string, unknown>).filter(
    (key) => key.length > 0 && !key.startsWith("$"),
  );
}

/**
 * Extract workflow and task IDs from a (resolved) agent-contracts DSL document.
 *
 * Reads workflow IDs from BOTH the canonical `workflow` (singular) key used by
 * the aaac DSL convention AND the legacy `workflows` (plural) key, unioned for
 * backward compatibility. Task IDs come from `tasks`. `$`-prefixed directive
 * keys (e.g. `$refs`) are excluded.
 */
export function extractImplementationIds(content: unknown): ImplementationIds {
  if (!content || typeof content !== "object" || Array.isArray(content)) {
    return { workflowIds: [], taskIds: [] };
  }

  const doc = content as Record<string, unknown>;
  const workflowIds = [
    ...extractIdsFromSection(doc.workflow),
    ...extractIdsFromSection(doc.workflows),
  ];
  return {
    workflowIds: Array.from(new Set(workflowIds)),
    taskIds: extractIdsFromSection(doc.tasks),
  };
}

async function fileExists(absolutePath: string): Promise<boolean> {
  try {
    await access(absolutePath);
    return true;
  } catch {
    return false;
  }
}

async function loadImplementationIds(
  implementationPath: string,
  basePath: string,
): Promise<ImplementationIds> {
  const absolutePath = resolve(basePath, implementationPath);

  if (!(await fileExists(absolutePath))) {
    throw new ComponentValidationError(
      `Implementation file not found: ${absolutePath}`,
    );
  }

  // Resolve the implementation DSL via the `agent-contracts` package so that
  // `extends`, `$ref`, and `$refs` are expanded — mirroring the runtime's
  // loadDsl() in @aaac/runtime. This makes component validation consistent with
  // how the runtime actually reads the DSL (single source of truth).
  let parsed: unknown;
  try {
    const { resolve: resolveDsl } = await import("agent-contracts");
    const result = await resolveDsl(absolutePath);
    parsed = result.data;
  } catch (err) {
    throw new ComponentValidationError(
      `Cannot resolve implementation DSL ${absolutePath}: ${(err as Error).message}`,
    );
  }

  return extractImplementationIds(parsed);
}

function formatAvailableIds(label: string, ids: string[]): string {
  if (ids.length === 0) {
    return `${label}: (none)`;
  }
  return `${label}: ${ids.join(", ")}`;
}

/**
 * Validate a compiled Component IR against its implementation DSL.
 *
 * Checks:
 * - implementation file exists (when specified)
 * - agentWorkflow IDs exist in implementation DSL
 * - agentTask IDs exist in implementation DSL
 */
export async function validateComponent(
  ir: ComponentIR,
  options: ValidationOptions,
): Promise<void> {
  const usesAgentic = Object.values(ir.operations).some(
    (op) => op.agentWorkflow !== undefined || op.agentTask !== undefined,
  );

  if (!ir.implementation) {
    if (usesAgentic) {
      throw new ComponentValidationError(
        "implementation is required when any operation uses agentWorkflow or agentTask",
      );
    }
    return;
  }

  const ids = await loadImplementationIds(ir.implementation, options.basePath);

  for (const [operationName, op] of Object.entries(ir.operations)) {
    if (op.agentWorkflow !== undefined) {
      if (!ids.workflowIds.includes(op.agentWorkflow)) {
        throw new ComponentValidationError(
          `Operation "${operationName}": agentWorkflow "${op.agentWorkflow}" not found in implementation DSL. ${formatAvailableIds("Available workflows", ids.workflowIds)}`,
        );
      }
    }

    if (op.agentTask !== undefined) {
      if (!ids.taskIds.includes(op.agentTask)) {
        throw new ComponentValidationError(
          `Operation "${operationName}": agentTask "${op.agentTask}" not found in implementation DSL. ${formatAvailableIds("Available tasks", ids.taskIds)}`,
        );
      }
    }
  }
}
